import React, {memo, useState} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import Grid from '@mui/material/Grid';
import Image from 'next/image';
import parse from 'html-react-parser';
import Collapse from '@mui/material/Collapse';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {colors, shadows, overlay, typography, fontSize, lineHeight, fontWeight, transition} from '@/styles/tokens';

interface TechStackProps {
    heading?: string;
    subheading?: string;
    techCategories: {
        id: string;
        heading: string;
        items: {label: string; src: any}[];
        whyWeUseThem?: string;
    }[];
}

const containerBoxSx = {
    display: 'flex',
    flexDirection: {xs: 'column', md: 'row'},
    borderRadius: 4,
    border: `1px solid ${colors.neutral.grayLight}`,
    boxShadow: shadows.elevated,
    bgcolor: colors.background.default,
    minHeight: 400
} as const;

const leftPaneSx = {
    width: {xs: '100%', md: 380},
    background: `linear-gradient(0deg, ${colors.primary.darkest} 0%, ${colors.primary.deeper} 50%, ${colors.primary.deep} 100%)`,
    p: {xs: 1.5, md: 3},
    display: 'flex',
    flexDirection: 'column',
    gap: {xs: 1.5, md: 2},
    borderRadius: {xs: '16px 16px 0 0', md: '16px 0 0 16px'}
} as const;

const rightPaneSx = {
    flex: 1,
    p: {xs: 3, md: 3, lg: 5},
    bgcolor: colors.background.default,
    display: {xs: 'none', md: 'block'},
    borderRadius: {md: '0 16px 16px 0'}
} as const;

const tabItemSx = (isActive: boolean) => ({
    width: '100%',
    textAlign: 'left',
    py: {xs: 2, md: 2},
    px: 3,
    borderRadius: 2,
    cursor: 'pointer',
    bgcolor: isActive ? colors.background.default : overlay.whiteSubtle,
    color: isActive ? colors.text.primary : colors.text.inverse,
    fontWeight: isActive ? fontWeight.bold : fontWeight.medium,
    fontSize: typography.bodySm.fontSize,
    transition: transition.base,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: {xs: `1px solid rgba(255,255,255,0.1)`, md: 'none'},
    '&:hover': {
        bgcolor: isActive ? colors.background.default : overlay.whiteMid
    }
});

const techItemCardSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    p: 2,
    border: `1px solid ${colors.neutral.borderLight}`,
    borderRadius: 2,
    bgcolor: colors.background.tag
} as const;

const mobilePaneSx = {
    mt: 0.5,
    mb: 1.5,
    bgcolor: colors.background.surfaceAlt,
    borderRadius: 2,
    p: 2
} as const;

const whyBoxSx = {
    mt: 4,
    pt: 3,
    borderTop: `1px solid ${colors.neutral.borderLight}`
} as const;

const whyMobileBoxSx = {
    mt: 2,
    pt: 2,
    borderTop: `1px solid ${colors.neutral.borderLight}`
} as const;

const whyLabelSx = {
    fontWeight: fontWeight.bold,
    fontSize: typography.body,
    mb: 1,
    color: colors.text.primary
} as const;

const whyMobileLabelSx = {
    fontWeight: fontWeight.bold,
    fontSize: fontSize[14],
    mb: 0.5,
    color: colors.text.primary
} as const;

const whyTextSx = {
    ...typography.bodySm,
    fontSize: fontSize[16],
    lineHeight: lineHeight[28],
    color: colors.text.primary
} as const;

const whyMobileTextSx = {
    fontSize: fontSize[16],
    lineHeight: lineHeight[24],
    color: colors.text.primary
} as const;

const TechStack = ({heading, subheading, techCategories}: TechStackProps) => {
    const [activeTab, setActiveTab] = useState<string | null>(techCategories[0]?.id || null);

    return (
        <Box>
            <MUIContainer>
                <Box textAlign="center" maxWidth={1024} margin="0 auto" mb={5}>
                    <Typography variant="h1" component="h2" mb={3} fontSize={{xs: 35, sm: 48}} className="font-headers">
                        {heading ? parse(heading) : ''}
                    </Typography>

                    <Typography
                        className="font-regular-text"
                        variant="subtitle1"
                        component="p"
                        sx={{mb: {xs: 2, md: 6}, fontSize: fontSize[16], color: colors.text.primary}}
                    >
                        {subheading ? parse(subheading) : ''}
                    </Typography>
                </Box>

                <Box sx={containerBoxSx}>
                    <Box sx={leftPaneSx}>
                        {techCategories?.map((category) => {
                            const isActive = activeTab === category.id;
                            return (
                                <Box key={category.id}>
                                    <Box sx={tabItemSx(isActive)} onClick={() => setActiveTab(isActive ? null : category.id)}>
                                        <Typography component="h3" className="font-headers">
                                            {category.heading}
                                        </Typography>
                                        <Box sx={{display: {xs: 'block', md: 'none'}}}>
                                            {isActive ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                        </Box>
                                    </Box>
                                    <Box sx={{display: {xs: 'block', md: 'none'}}}>
                                        <Collapse in={isActive} timeout="auto" unmountOnExit>
                                            <Box sx={mobilePaneSx}>
                                                <Grid container spacing={2}>
                                                    {category.items?.map((item, idx) => (
                                                        <Grid item xs={12} sm={6} key={idx}>
                                                            <Box sx={techItemCardSx}>
                                                                <Box
                                                                    sx={{
                                                                        width: 30,
                                                                        height: 30,
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center'
                                                                    }}
                                                                >
                                                                    <Image src={item.src} alt={item.label} width={32} height={32} />
                                                                </Box>
                                                                <Typography
                                                                    className="font-regular-text"
                                                                    sx={{fontSize: fontSize[16], color: colors.text.primary}}
                                                                >
                                                                    {item.label}
                                                                </Typography>
                                                            </Box>
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                                {category.whyWeUseThem && (
                                                    <Box sx={whyMobileBoxSx}>
                                                        <Typography className="font-headers" sx={whyMobileLabelSx}>
                                                            Why we use them:
                                                        </Typography>
                                                        <Typography className="font-regular-text" sx={whyMobileTextSx}>
                                                            {category.whyWeUseThem}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Collapse>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                    {/* Desktop right pane — render EVERY category's tech items and
                        "Why we use them" text so all of it is in the server HTML for
                        crawlers (SEO #33); only the active category is shown via CSS. */}
                    <Box sx={rightPaneSx}>
                        {techCategories?.map((category) => (
                            <Box key={category.id} sx={{display: activeTab === category.id ? 'block' : 'none'}}>
                                <Grid container spacing={{xs: 2, md: 3}}>
                                    {category.items?.map((item, idx) => (
                                        <Grid item xs={12} sm={6} md={4} key={idx}>
                                            <Box sx={techItemCardSx}>
                                                <Box
                                                    sx={{
                                                        width: 40,
                                                        height: 40,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}
                                                >
                                                    <Image src={item.src} alt={item.label} width={32} height={32} />
                                                </Box>
                                                <Typography
                                                    className="font-regular-text remove-underline"
                                                    sx={{fontSize: fontSize[16], color: colors.text.primary}}
                                                >
                                                    {parse(item.label)}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                                {category.whyWeUseThem && (
                                    <Box sx={whyBoxSx}>
                                        <Typography className="font-headers" sx={whyLabelSx}>
                                            Why we use them:
                                        </Typography>
                                        <Typography className="font-regular-text" sx={whyTextSx}>
                                            {category.whyWeUseThem}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        ))}
                    </Box>
                </Box>
            </MUIContainer>
        </Box>
    );
};

export default memo(TechStack);
